import * as Sentry from '@sentry/nextjs'

let visitorIdPromise: Promise<string | null> | null = null

const FINGERPRINT_API_KEY = process.env.NEXT_PUBLIC_FINGERPRINT_API_KEY

/**
 * Load the FingerprintJS Pro agent and resolve its visitorId.
 * Cached so the agent is only loaded once per page session.
 * Never throws — returns null on any failure.
 */
function loadVisitorId(): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)

  if (!FINGERPRINT_API_KEY) {
    console.warn('[Fingerprint] NEXT_PUBLIC_FINGERPRINT_API_KEY not configured')
    return Promise.resolve(null)
  }

  return import('@fingerprintjs/fingerprintjs-pro')
    .then(FingerprintJS =>
      FingerprintJS.load({
        apiKey: FINGERPRINT_API_KEY,
        endpoint: 'https://bouncer.martini.film',
      })
        .then(agent => agent.get())
        .then(result => result.visitorId)
        .catch(err => {
          reportAgentFailure(err, [
            FingerprintJS.ERROR_SCRIPT_LOAD_FAIL,
            FingerprintJS.ERROR_CSP_BLOCK,
            FingerprintJS.ERROR_CLIENT_TIMEOUT,
            FingerprintJS.ERROR_NETWORK_CONNECTION,
            FingerprintJS.ERROR_NETWORK_ABORT,
            FingerprintJS.ERROR_NETWORK_RESTRICTED,
          ])
          return null
        }),
    )
    .catch(err => {
      // The SDK chunk itself failed to load, so its error constants aren't available.
      reportAgentFailure(err, [])
      return null
    })
}

/**
 * The agent fails on the visitor's side far more often than ours: ad blockers
 * stop the script loading, firewalls restrict it, and flaky networks drop or
 * time out its request. Those (and anything while the browser is offline)
 * produced ~18k Sentry events that nobody can act on, so they stay a breadcrumb. Anything else (a bad key, forbidden origin, rate limit, a proxy
 * failure at bouncer.martini.film) is our configuration and still reports.
 */
function reportAgentFailure(err: unknown, clientSideReasons: readonly string[]): void {
  const reason = err instanceof Error ? err.message : String(err)
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  if (offline || clientSideReasons.includes(reason)) {
    Sentry.addBreadcrumb({
      category: 'fingerprint',
      message: `Fingerprint agent unavailable: ${reason}`,
      level: 'info',
    })
    return
  }
  Sentry.captureMessage('Fingerprint agent failed', {
    level: 'warning',
    tags: { source: 'fingerprint' },
    extra: { reason },
  })
}

/**
 * Get the device visitorId. Safe to call multiple times — the agent is
 * loaded once and the result is cached. Returns null if the agent fails
 * to load or produce a result (ad-blocker, network error, etc.).
 */
export async function getVisitorId(): Promise<string | null> {
  if (!visitorIdPromise) {
    visitorIdPromise = loadVisitorId()
  }
  return visitorIdPromise
}
